import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { EASE } from "./Reveal";

const HERO_IMG = "/assets/sunset-woods.png";

const line = (i) => ({
  initial: { y: "115%" },
  animate: { y: "0%" },
  transition: { duration: 1.15, delay: 1.55 + i * 0.16, ease: EASE },
});

const fade = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: EASE },
});

const Hero = ({ onNav }) => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 260]);
  const bgScale = useTransform(scrollY, [0, 900], [1.05, 1.22]);
  const contentOpacity = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section id="top" data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={HERO_IMG}
          alt="Dark forest atmosphere"
          className="w-full h-full object-cover analog-grade"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/55 to-ink" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(28,30,27,0.55)_100%)]" />
      <div className="absolute inset-0 z-[5] pointer-events-none" aria-hidden="true">
        {[
          { left: "58%", top: "18%", size: 4, delay: "0s", duration: "10s" },
          { left: "72%", top: "28%", size: 5, delay: "2.6s", duration: "9s" },
          { left: "84%", top: "16%", size: 3, delay: "4.2s", duration: "11s" },
          { left: "66%", top: "38%", size: 4, delay: "1.4s", duration: "9.6s" },
        ].map((f, i) => (
          <span
            key={i}
            data-testid={`hero-firefly-${i}`}
            className="firefly"
            style={{
              left: f.left,
              top: f.top,
              width: f.size,
              height: f.size,
              animationDelay: f.delay,
              animationDuration: f.duration,
            }}
          />
        ))}
      </div>

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-24 lg:pb-28"
      >
        <motion.p {...fade(1.35)} className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold mb-6">
          Independent Artist — Director — Filmmaker
        </motion.p>

        <h1 className="font-serif font-light leading-[0.88] tracking-tight text-cream" data-testid="hero-headline">
          <span className="block overflow-hidden">
            <motion.span {...line(0)} className="block text-[19vw] md:text-[15vw] lg:text-[11.5rem]">
              DANI
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span {...line(1)} className="block text-[19vw] md:text-[15vw] lg:text-[11.5rem]">
              MORR
              <svg
                viewBox="0 0 24 24"
                className="inline-block w-[0.32em] h-[0.32em] ml-[0.08em] -translate-y-[0.9em]"
                fill="#C18C3D"
                aria-hidden="true"
              >
                <path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" />
              </svg>
            </motion.span>
          </span>
        </h1>

        <div className="mt-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <div className="max-w-xl">
            <motion.h2
              {...fade(2.05)}
              data-testid="hero-subheadline"
              className="font-serif italic text-xl md:text-2xl text-gold mb-4"
            >
              Sonic Architecture. Cinematic Worldbuilding.
            </motion.h2>
            <motion.p {...fade(2.2)} className="text-sm md:text-base text-cream/70 leading-relaxed">
              Multi-hyphenate artist, songwriter, music producer, and filmmaker crafting
              immersive sensory landscapes — where analog nostalgia meets forward-thinking
              digital production, soulful soundscapes, and vivid dark-fantasy storytelling.
            </motion.p>
          </div>

          <motion.div {...fade(2.35)} className="flex flex-wrap items-center gap-4" data-testid="hero-ctas">
            <button
              data-testid="cta-explore-music"
              onClick={() => onNav("music")}
              className="group flex items-center gap-2 bg-amber text-cream font-mono text-[11px] uppercase tracking-[0.25em] px-7 py-4 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              Explore Music
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="cta-watch-visuals"
              onClick={() => onNav("cinematic")}
              className="group flex items-center gap-2 border border-cream/30 text-cream font-mono text-[11px] uppercase tracking-[0.25em] px-7 py-4 rounded-full hover:border-gold hover:text-gold transition-colors duration-300"
            >
              Watch Visuals
            </button>
            <button
              data-testid="cta-view-projects"
              onClick={() => onNav("cinematic")}
              className="group flex items-center gap-2 border border-cream/30 text-cream font-mono text-[11px] uppercase tracking-[0.25em] px-7 py-4 rounded-full hover:border-gold hover:text-gold transition-colors duration-300"
            >
              View Projects
            </button>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        {...fade(2.6)}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-cream/40"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ArrowDown size={14} />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
